// Core domain types for the entire Statura Care platform

// ── Tenant ──
export interface Tenant {
  id: string;
  name: string;
  abn: string;
  legalName: string;
  registrationType: 'registered_provider' | 'associated_provider';
  registrationId?: string;
  address: Address;
  phone: string;
  email: string;
  website?: string;
  logoUrl?: string;
  brandConfig?: Record<string, unknown>;
  subscriptionTier: 'standard' | 'professional' | 'enterprise';
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ── Address ──
export interface Address {
  street: string;
  suburb: string;
  state: AustralianState;
  postcode: string;
  lat?: number;
  lng?: number;
}

export type AustralianState = 'NSW' | 'VIC' | 'QLD' | 'WA' | 'SA' | 'TAS' | 'ACT' | 'NT';

// ── Legislative Control ──

export type LegislativeControlStatus =
  | 'automated'
  | 'manual_input_required'
  | 'external_submission_required'
  | 'not_supported';

export interface SourceVerificationMeta {
  sourceId: string;
  authority:
    | 'department_health'
    | 'my_aged_care'
    | 'services_australia'
    | 'fair_work'
    | 'aihw'
    | 'dss'
    | 'acqsc';
  title: string;
  verifiedOn: string;
  effectiveFrom?: string;
  effectiveTo?: string;
  sourceUrl?: string;
  notes?: string;
}

export interface ManualObligationStep {
  id: string;
  title: string;
  description: string;
  ownerRole?: string;
  evidenceHint?: string;
  externalSystem?: string;
  required: boolean;
}

export interface LegislativeControlRecord {
  controlId: string;
  modulePath: string;
  title: string;
  obligation: string;
  effectiveDateLogic: string;
  status: LegislativeControlStatus;
  dataModels: string[];
  sourceIds: string[];
  notes?: string;
  manualSteps?: ManualObligationStep[];
}

// ── User ──
export type UserRole =
  | 'SYSTEM_ADMIN'
  | 'TENANT_ADMIN'
  | 'CEO'
  | 'OWNER'
  | 'OPERATIONS_DIRECTOR'
  | 'CLINICAL_DIRECTOR'
  | 'FINANCE_DIRECTOR'
  | 'QUALITY_DIRECTOR'
  | 'HR_DIRECTOR'
  | 'REGION_MANAGER'
  | 'REGION_COMPLIANCE_OFFICER'
  | 'REGION_FINANCE_OFFICER'
  | 'COORDINATOR'
  | 'TEAM_LEADER'
  | 'REGISTERED_NURSE'
  | 'ENROLLED_NURSE'
  | 'CARE_WORKER'
  | 'ALLIED_HEALTH'
  | 'FINANCE_OFFICER'
  | 'PAYROLL_OFFICER'
  | 'CLIENT'
  | 'FAMILY_CARER'
  | 'CLINICAL_LEAD'
  | 'AGENCY_CONTRACTOR'
  | 'FACILITY_MANAGER'
  | 'QUALITY_OFFICER'
  | 'COMPLIANCE_OFFICER'
  | 'CASE_MANAGER'
  | 'LIFESTYLE_OFFICER'
  | 'HR_OFFICER';

/**
 * Runtime companion to UserRole. Keep role parsing and assignment maps tied
 * to the same canonical vocabulary instead of repeating partial local sets.
 */
export const USER_ROLE_VALUES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'CEO',
  'OWNER',
  'OPERATIONS_DIRECTOR',
  'CLINICAL_DIRECTOR',
  'FINANCE_DIRECTOR',
  'QUALITY_DIRECTOR',
  'HR_DIRECTOR',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
  'COORDINATOR',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'ALLIED_HEALTH',
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
  'CLIENT',
  'FAMILY_CARER',
  'CLINICAL_LEAD',
  'AGENCY_CONTRACTOR',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'CASE_MANAGER',
  'LIFESTYLE_OFFICER',
  'HR_OFFICER',
] as const satisfies readonly UserRole[];

/**
 * Provider access roles admitted without an employment record by the SQL
 * is_internal_staff_actor gate. This is an explicit allowlist: new roles need
 * review. AGENCY_CONTRACTOR keeps the active staff-row requirement; consumers
 * use portal authority. Keep the migration and parity proof in lockstep.
 */
export const STAFFLESS_INTERNAL_USER_ROLE_VALUES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'CEO',
  'OWNER',
  'OPERATIONS_DIRECTOR',
  'CLINICAL_DIRECTOR',
  'FINANCE_DIRECTOR',
  'QUALITY_DIRECTOR',
  'HR_DIRECTOR',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
  'COORDINATOR',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'CARE_WORKER',
  'ALLIED_HEALTH',
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
  'CLINICAL_LEAD',
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
  'COMPLIANCE_OFFICER',
  'CASE_MANAGER',
  'LIFESTYLE_OFFICER',
  'HR_OFFICER',
] as const satisfies readonly UserRole[];

/** Runtime parser for persisted user-role tokens. Keep assignment policy separate. */
export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (USER_ROLE_VALUES as readonly string[]).includes(value);
}

/** Parse a persisted role token without turning unknown values into authority. */
export function parseUserRole(value: unknown): UserRole | null {
  return isUserRole(value) ? value : null;
}

export * from './notification-policy';
export * from './notification-policy-configuration';

export type WorkflowType =
  | 'COMPLIANCE_CAPTURE_URGENT'
  | 'ESCALATION'
  | 'SIRS_INCIDENT'
  | 'DATA_BREACH'
  | 'IPC_OUTBREAK'
  | 'SCREENING_EXPIRY'
  | 'SIRS_SCOPE_REVIEW_REQUIRED'
  | 'NON_SIRS_INCIDENT'
  | 'MESSAGE'
  | 'SHIFT_HANDOVER'
  | 'LEAVE_REQUEST'
  | 'ABSENCE_LEAVE_REQUIRED'
  | 'LEAVE_EVIDENCE_DUE'
  | 'SHIFT_SWAP_REQUEST'
  | 'TASK_COMPLETION'
  | 'RESTRICTIVE_PRACTICE_USE'
  | 'WOUND_DETERIORATION'
  | 'WHS_INCIDENT'
  | 'CLINICAL_ALERT'
  | 'MEDICATION_INCIDENT'
  | 'OPEN_DISCLOSURE'
  | 'SIRS_INVESTIGATION_REPORT'
  | 'UNEXPLAINED_ABSENCE'
  | 'SYSTEM_ALERT'
  | 'WORKFORCE_COMPLIANCE'
  | 'WORKFORCE_POLICY_ASSIGNED'
  | 'WORKFORCE_POLICY_REMINDER'
  | 'ESCALATION_EXHAUSTED'
  | 'WORKFORCE_REPLACE_ON_SICK_DAY'
  | 'RAC_ADMISSION'
  | 'RAC_DISCHARGE'
  | 'RAC_RESPITE'
  | 'SAH_ONBOARDING'
  | 'COMPLAINT'
  | 'CARE_PLAN_REVIEW'
  | 'REGULATOR_SUBMISSION_REMINDER'
  | 'ROSTER_RUNTIME_SHIFT_LATE'
  | 'ROSTER_RUNTIME_SHIFT_NO_SHOW'
  // People lifecycle S1.5a (20303600000000): the D17 legal-floor punch alert.
  | 'WORKER_ASSIGNMENT_FLOOR_PUNCH_ALERT'
  | 'ROSTER_RUNTIME_SHIFT_AWAITING_CLOCK'
  | 'ROSTER_RUNTIME_MISSED_CLOCK_OUT'
  | 'TIMESHEET_REJECTED'
  | 'WORKFORCE_WORKED_TIME_PARTIALLY_APPROVED'
  | 'SAH_BUDGET_VARIANCE'
  | 'CLIENT_FUNDING_TRANSITION'
  | 'SAH_EOL_ACTIVATION'
  // B-17 ops thresholds (Phase A regulatory alerts surfaced via real-time ops)
  | 'OPS_CARE_MINUTES_SHORTFALL'
  | 'OPS_RN_COVERAGE_MID_PERIOD_GAP'
  | 'OPS_RP_AUTHORISATION_LAPSING'
  | 'OPS_RESIDENT_CLINICAL_ALERT'
  // R0.4-b-recovery: a regulated capture stranded on a shared mobile device
  // (captor left without syncing) uploaded to the coordinator-only quarantine.
  | 'STRANDED_REGULATED_CAPTURE';

export type Channel = 'in_app' | 'push' | 'email' | 'sms';

export type PriorityClass =
  | 'CRITICAL'
  | 'REGULATORY'
  | 'CLINICAL'
  | 'IMPORTANT'
  | 'APPROVAL'
  | 'STANDARD'
  | 'INFORMATIONAL';

export type WorkflowPreferenceCategory =
  | 'clinical'
  | 'compliance'
  | 'incidents'
  | 'roster'
  | 'tasks'
  | 'admin'
  | 'system';

export interface WorkflowTypeMetadata {
  priority: PriorityClass;
  requiresAck: boolean;
  bypassDndDefault: boolean;
  prefCategory: WorkflowPreferenceCategory;
}

export const WORKFLOW_TYPE_META: Record<WorkflowType, WorkflowTypeMetadata> = {
  DATA_BREACH: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'compliance',
  },
  IPC_OUTBREAK: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  SCREENING_EXPIRY: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  ESCALATION: {
    priority: 'CRITICAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'clinical',
  },
  SIRS_INCIDENT: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'incidents',
  },
  SIRS_SCOPE_REVIEW_REQUIRED: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'incidents',
  },
  NON_SIRS_INCIDENT: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'incidents',
  },
  MESSAGE: {
    priority: 'STANDARD',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'admin',
  },
  SHIFT_HANDOVER: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'roster',
  },
  LEAVE_REQUEST: {
    priority: 'APPROVAL',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'roster',
  },
  ABSENCE_LEAVE_REQUIRED: {
    priority: 'IMPORTANT',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'roster',
  },
  LEAVE_EVIDENCE_DUE: {
    priority: 'IMPORTANT',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'roster',
  },
  SHIFT_SWAP_REQUEST: {
    priority: 'APPROVAL',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'roster',
  },
  TASK_COMPLETION: {
    priority: 'INFORMATIONAL',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'tasks',
  },
  RESTRICTIVE_PRACTICE_USE: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'incidents',
  },
  WOUND_DETERIORATION: {
    priority: 'CLINICAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'clinical',
  },
  // Provider escalation process; this priority does not assert legal reportability.
  COMPLIANCE_CAPTURE_URGENT: {
    priority: 'CRITICAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'incidents',
  },
  WHS_INCIDENT: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'incidents',
  },
  CLINICAL_ALERT: {
    priority: 'CLINICAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'clinical',
  },
  MEDICATION_INCIDENT: {
    priority: 'CLINICAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'clinical',
  },
  OPEN_DISCLOSURE: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'incidents',
  },
  SIRS_INVESTIGATION_REPORT: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'incidents',
  },
  UNEXPLAINED_ABSENCE: {
    priority: 'CRITICAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'incidents',
  },
  SYSTEM_ALERT: {
    priority: 'IMPORTANT',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'system',
  },
  WORKFORCE_COMPLIANCE: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  WORKFORCE_POLICY_ASSIGNED: {
    priority: 'STANDARD',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  WORKFORCE_POLICY_REMINDER: {
    priority: 'IMPORTANT',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  ESCALATION_EXHAUSTED: {
    priority: 'CRITICAL',
    requiresAck: false,
    bypassDndDefault: true,
    prefCategory: 'system',
  },
  WORKFORCE_REPLACE_ON_SICK_DAY: {
    priority: 'APPROVAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'roster',
  },
  RAC_ADMISSION: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  RAC_DISCHARGE: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  RAC_RESPITE: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  SAH_ONBOARDING: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  COMPLAINT: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  CARE_PLAN_REVIEW: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'clinical',
  },
  REGULATOR_SUBMISSION_REMINDER: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'compliance',
  },
  ROSTER_RUNTIME_SHIFT_LATE: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'roster',
  },
  ROSTER_RUNTIME_SHIFT_NO_SHOW: {
    priority: 'CRITICAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'roster',
  },
  // Urgent statutory-floor alert raised when a worker clocks in against the
  // D17 legal floor; routed per tenant with bypass_quiet_hours (S1.5a).
  WORKER_ASSIGNMENT_FLOOR_PUNCH_ALERT: {
    priority: 'CRITICAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'compliance',
  },
  ROSTER_RUNTIME_SHIFT_AWAITING_CLOCK: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'roster',
  },
  // Daily Time Review v1 (2026-07-14): missed clock-out is end-of-day review
  // work, not a wake-someone-up event; rejection is a personal worker notice.
  ROSTER_RUNTIME_MISSED_CLOCK_OUT: {
    priority: 'IMPORTANT',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'roster',
  },
  TIMESHEET_REJECTED: {
    priority: 'APPROVAL',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'roster',
  },
  WORKFORCE_WORKED_TIME_PARTIALLY_APPROVED: {
    priority: 'APPROVAL',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'roster',
  },
  SAH_BUDGET_VARIANCE: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'compliance',
  },
  CLIENT_FUNDING_TRANSITION: {
    priority: 'APPROVAL',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'admin',
  },
  SAH_EOL_ACTIVATION: {
    priority: 'APPROVAL',
    requiresAck: false,
    bypassDndDefault: false,
    prefCategory: 'admin',
  },
  // B-17 ops thresholds (Phase A regulatory alerts) — REGULATORY priority,
  // require acknowledgement, bypass DND because they surface live shortfalls.
  OPS_CARE_MINUTES_SHORTFALL: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'compliance',
  },
  OPS_RN_COVERAGE_MID_PERIOD_GAP: {
    priority: 'CRITICAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'roster',
  },
  OPS_RP_AUTHORISATION_LAPSING: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: false,
    prefCategory: 'compliance',
  },
  OPS_RESIDENT_CLINICAL_ALERT: {
    priority: 'CLINICAL',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'clinical',
  },
  // R0.4-b-recovery: a stranded regulated capture needs a coordinator to
  // reconcile it before its SIRS/S8 SLA lapses — regulatory, must be
  // acknowledged, bypasses DND. Notification body is PHI-free (metadata only).
  STRANDED_REGULATED_CAPTURE: {
    priority: 'REGULATORY',
    requiresAck: true,
    bypassDndDefault: true,
    prefCategory: 'incidents',
  },
};

export const STORM_BATCH_BLOCKLIST: ReadonlyArray<WorkflowType> = [
  'COMPLIANCE_CAPTURE_URGENT',
  'SIRS_INCIDENT',
  'DATA_BREACH',
  'IPC_OUTBREAK',
  'SCREENING_EXPIRY',
  'SIRS_SCOPE_REVIEW_REQUIRED',
  // R0.4-b-recovery: a stranded regulated capture is a regulatory obligation on
  // an SLA clock — never batch/suppress it.
  'STRANDED_REGULATED_CAPTURE',
  'RESTRICTIVE_PRACTICE_USE',
  'OPEN_DISCLOSURE',
  'SIRS_INVESTIGATION_REPORT',
  'UNEXPLAINED_ABSENCE',
  'ESCALATION',
  'ESCALATION_EXHAUSTED',
  'WORKFORCE_REPLACE_ON_SICK_DAY',
  'RAC_ADMISSION',
  'RAC_DISCHARGE',
  'SAH_ONBOARDING',
  'WORKFORCE_COMPLIANCE',
  'COMPLAINT',
  'CARE_PLAN_REVIEW',
  'REGULATOR_SUBMISSION_REMINDER',
  'ROSTER_RUNTIME_SHIFT_LATE',
  'ROSTER_RUNTIME_SHIFT_NO_SHOW',
  'ROSTER_RUNTIME_SHIFT_AWAITING_CLOCK',
  'ROSTER_RUNTIME_MISSED_CLOCK_OUT',
  'TIMESHEET_REJECTED',
  'WORKFORCE_WORKED_TIME_PARTIALLY_APPROVED',
  'SAH_BUDGET_VARIANCE',
  'CLIENT_FUNDING_TRANSITION',
  'SAH_EOL_ACTIVATION',
];

export type WorkflowCapabilityTier = 'mandatory' | 'strongly_recommended' | 'optional';
export type WorkflowSurface = 'web' | 'care-worker-app' | 'client-portal';
export type PortalAccessLevel = 'CLIENT' | 'NOMINEE' | 'CONTACT_PERSON';
export type PortalSectionCategory =
  | 'overview'
  | 'care'
  | 'safety'
  | 'financial'
  | 'communication'
  | 'account';
export type PortalSection =
  | 'dashboard'
  | 'care_plan'
  | 'visits'
  | 'medications'
  | 'incidents'
  | 'restrictive_practices'
  | 'advance_care'
  | 'budget'
  | 'invoices'
  | 'messages'
  | 'feedback'
  | 'my_rights'
  | 'my_agreement'
  | 'documents'
  | 'profile';

export type WorkflowCapabilityId =
  | 'tenant.configure'
  | 'client.create'
  | 'client.edit'
  | 'client.care.workspace'
  | 'frontline.shift.execute'
  | 'frontline.handover.access'
  | 'frontline.handover.signoff'
  | 'frontline.observation.record'
  | 'frontline.medication.administer'
  | 'frontline.wound.record'
  | 'incident.create'
  | 'incident.review'
  | 'complaint.create'
  | 'service.delivery.manage'
  | 'service.execution.reconcile'
  | 'roster.manage'
  | 'sah.claim.prepare'
  | 'sah.statement.issue'
  | 'sah.athm.manage'
  | 'workforce.payroll.review'
  | 'prudential.access'
  | 'quality.gpms.access'
  | 'dex.reporting'
  | 'analytics.access'
  | 'portal.care.view'
  | 'portal.safety.view'
  | 'portal.financial.view'
  | 'portal.messaging'
  | 'portal.feedback'
  | 'portal.documents.view'
  | 'portal.rights.view';

export interface WorkflowCapabilityDefinition {
  id: WorkflowCapabilityId;
  label: string;
  description: string;
  tier: WorkflowCapabilityTier;
  ownerSurface: WorkflowSurface;
  surfaces: readonly WorkflowSurface[];
  tenantConfigurable: boolean;
}

export const WORKFLOW_CAPABILITY_DEFINITIONS: readonly WorkflowCapabilityDefinition[] = [
  {
    id: 'tenant.configure',
    label: 'Tenant configuration',
    description: 'Configure modules, workflow templates, approvals, and local operating rules.',
    tier: 'strongly_recommended',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: false,
  },
  {
    id: 'client.create',
    label: 'Client creation',
    description: 'Create new clients or residents in the canonical registry.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: false,
  },
  {
    id: 'client.edit',
    label: 'Client maintenance',
    description: 'Maintain canonical client demographics, contacts, and status.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: false,
  },
  {
    id: 'client.care.workspace',
    label: 'Client care workspace',
    description:
      'View and work with client care records across care plans, goals, and assessments.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web', 'care-worker-app'],
    tenantConfigurable: false,
  },
  {
    id: 'frontline.shift.execute',
    label: 'Frontline shift execution',
    description:
      'Execute rostered shifts or visits, including clocking, tasks, and point-of-care capture.',
    tier: 'mandatory',
    ownerSurface: 'care-worker-app',
    surfaces: ['care-worker-app', 'web'],
    tenantConfigurable: false,
  },
  {
    id: 'frontline.handover.access',
    label: 'Frontline handover access',
    description: 'Author and view handovers needed for shift-to-shift continuity.',
    tier: 'mandatory',
    ownerSurface: 'care-worker-app',
    surfaces: ['care-worker-app', 'web'],
    tenantConfigurable: false,
  },
  {
    id: 'frontline.handover.signoff',
    label: 'Frontline handover sign-off',
    description: 'Review and sign off clinically material or operationally important handovers.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: false,
  },
  {
    id: 'frontline.observation.record',
    label: 'Observation recording',
    description: 'Capture and review observations and abnormal values from frontline care.',
    tier: 'mandatory',
    ownerSurface: 'care-worker-app',
    surfaces: ['care-worker-app', 'web'],
    tenantConfigurable: false,
  },
  {
    id: 'frontline.medication.administer',
    label: 'Medication administration',
    description: 'Administer and review medications within role and policy limits.',
    tier: 'mandatory',
    ownerSurface: 'care-worker-app',
    surfaces: ['care-worker-app', 'web'],
    tenantConfigurable: false,
  },
  {
    id: 'frontline.wound.record',
    label: 'Wound recording',
    description: 'Capture wound updates and feed them into web-based review workflows.',
    tier: 'mandatory',
    ownerSurface: 'care-worker-app',
    surfaces: ['care-worker-app', 'web'],
    tenantConfigurable: false,
  },
  {
    id: 'incident.create',
    label: 'Incident creation',
    description: 'Lodge incidents from frontline or web workspaces.',
    tier: 'mandatory',
    ownerSurface: 'care-worker-app',
    surfaces: ['care-worker-app', 'web'],
    tenantConfigurable: false,
  },
  {
    id: 'incident.review',
    label: 'Incident review',
    description: 'Review, investigate, classify, and close incidents with evidence.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: false,
  },
  {
    id: 'complaint.create',
    label: 'Complaint and feedback intake',
    description: 'Lodge complaints or feedback from portal or internal workflows.',
    tier: 'mandatory',
    ownerSurface: 'client-portal',
    surfaces: ['client-portal', 'web'],
    tenantConfigurable: false,
  },
  {
    id: 'service.delivery.manage',
    label: 'Service delivery management',
    description: 'Schedule and manage service delivery and visit execution from the web workspace.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web', 'care-worker-app', 'client-portal'],
    tenantConfigurable: false,
  },
  {
    id: 'service.execution.reconcile',
    label: 'Service execution reconciliation',
    description:
      'Review preserved service-execution submissions and commit or resolve governed evidence.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web', 'care-worker-app'],
    tenantConfigurable: false,
  },
  {
    id: 'roster.manage',
    label: 'Roster management',
    description: 'Manage rosters, swaps, leave impacts, and staffing continuity.',
    tier: 'strongly_recommended',
    ownerSurface: 'web',
    surfaces: ['web', 'care-worker-app'],
    tenantConfigurable: true,
  },
  {
    id: 'sah.claim.prepare',
    label: 'SAH claim preparation',
    description: 'Prepare and remediate Support at Home claims with source-row traceability.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: false,
  },
  {
    id: 'sah.statement.issue',
    label: 'SAH statement issuing',
    description: 'Prepare, issue, resend, and review Support at Home statements.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web', 'client-portal'],
    tenantConfigurable: false,
  },
  {
    id: 'sah.athm.manage',
    label: 'SAH AT-HM management',
    description: 'Manage the AT-HM request, approval, and completion lifecycle.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: false,
  },
  {
    id: 'workforce.payroll.review',
    label: 'Payroll readiness review',
    description: 'Review payroll readiness, disputes, and approved timesheet export state.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web', 'care-worker-app'],
    tenantConfigurable: false,
  },
  {
    id: 'prudential.access',
    label: 'Prudential and residential commercial access',
    description: 'Manage prudential, accommodation pricing, governance, and attestation workflows.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: false,
  },
  {
    id: 'quality.gpms.access',
    label: 'Quality and GPMS access',
    description: 'Manage GPMS, quality indicators, and quality governance workflows.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: false,
  },
  {
    id: 'dex.reporting',
    label: 'DEX reporting',
    description: 'Prepare and review DEX reporting outputs and reconciliations.',
    tier: 'mandatory',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: false,
  },
  {
    id: 'analytics.access',
    label: 'Analytics access',
    description: 'Access operational and strategic analytics dashboards.',
    tier: 'optional',
    ownerSurface: 'web',
    surfaces: ['web'],
    tenantConfigurable: true,
  },
  {
    id: 'portal.care.view',
    label: 'Portal care visibility',
    description: 'Allow clients and families to view care plans, visits, and medication context.',
    tier: 'mandatory',
    ownerSurface: 'client-portal',
    surfaces: ['client-portal'],
    tenantConfigurable: false,
  },
  {
    id: 'portal.safety.view',
    label: 'Portal safety visibility',
    description: 'Allow clients and families to view safety-related portal sections.',
    tier: 'mandatory',
    ownerSurface: 'client-portal',
    surfaces: ['client-portal'],
    tenantConfigurable: false,
  },
  {
    id: 'portal.financial.view',
    label: 'Portal financial visibility',
    description: 'Allow clients and families to view statements, invoices, and budget artefacts.',
    tier: 'mandatory',
    ownerSurface: 'client-portal',
    surfaces: ['client-portal'],
    tenantConfigurable: false,
  },
  {
    id: 'portal.messaging',
    label: 'Portal messaging',
    description: 'Allow clients and families to send and receive messages with the provider.',
    tier: 'strongly_recommended',
    ownerSurface: 'client-portal',
    surfaces: ['client-portal', 'web'],
    tenantConfigurable: true,
  },
  {
    id: 'portal.feedback',
    label: 'Portal feedback and complaint intake',
    description: 'Allow clients and families to provide feedback and submit complaints.',
    tier: 'mandatory',
    ownerSurface: 'client-portal',
    surfaces: ['client-portal', 'web'],
    tenantConfigurable: false,
  },
  {
    id: 'portal.documents.view',
    label: 'Portal document visibility',
    description: 'Allow clients and families to view agreements, rights, and shared documents.',
    tier: 'mandatory',
    ownerSurface: 'client-portal',
    surfaces: ['client-portal'],
    tenantConfigurable: false,
  },
  {
    id: 'portal.rights.view',
    label: 'Portal rights visibility',
    description: 'Allow clients and families to view rights and key legislative information.',
    tier: 'mandatory',
    ownerSurface: 'client-portal',
    surfaces: ['client-portal'],
    tenantConfigurable: false,
  },
] as const;

export function getWorkflowCapabilityDefinition(
  capabilityId: WorkflowCapabilityId,
): WorkflowCapabilityDefinition {
  const definition = WORKFLOW_CAPABILITY_DEFINITIONS.find((entry) => entry.id === capabilityId);
  if (!definition) {
    throw new Error(`Unknown workflow capability: ${capabilityId}`);
  }
  return definition;
}

export function listWorkflowCapabilitiesBySurface(
  surface: WorkflowSurface,
): WorkflowCapabilityDefinition[] {
  return WORKFLOW_CAPABILITY_DEFINITIONS.filter((entry) => entry.surfaces.includes(surface));
}

export function isMandatoryWorkflowCapability(capabilityId: WorkflowCapabilityId): boolean {
  return getWorkflowCapabilityDefinition(capabilityId).tier === 'mandatory';
}

export interface User {
  id: string;
  tenantId: string;
  auth0Id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: UserRole;
  permissions: string[];
  avatarUrl?: string;
  isActive: boolean;
  lastLoginAt?: Date;
  mfaEnabled: boolean;
}

// ── Client ──
export type ClientStatus =
  | 'WAITLIST'
  | 'ASSESSMENT'
  | 'ACTIVE'
  | 'ON_HOLD'
  | 'HOSPITAL'
  | 'RESPITE'
  | 'DISCHARGED'
  | 'DECEASED'
  | 'ANONYMISED';
export type FundingProgram =
  | 'support_at_home'
  | 'chsp'
  | 'natsifacp'
  | 'ndis'
  | 'an_acc'
  | 'dva'
  | 'tac'
  | 'private';
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
export type IndigenousStatus =
  | 'aboriginal'
  | 'torres_strait_islander'
  | 'both'
  | 'neither'
  | 'not_stated';

export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
  isPrimary: boolean;
}

export interface Client {
  id: string;
  tenantId: string;
  macId?: string;
  ihi?: string;
  firstName: string;
  lastName: string;
  preferredName?: string;
  dateOfBirth: Date;
  gender: string;
  pronouns?: string;
  primaryAddress: Address;
  postalAddress?: Address;
  phone?: string;
  email?: string;
  emergencyContacts: EmergencyContact[];
  culturalBackground?: string;
  countryOfBirth?: string;
  languagePreferred: string;
  interpreterRequired: boolean;
  indigenousStatus?: IndigenousStatus;
  fundingPrograms: FundingProgram[];
  classificationLevel?: number;
  quarterlyBudget?: number;
  careManagementBudget?: number;
  pensionStatus?: string;
  status: ClientStatus;
  riskLevel?: RiskLevel;
  admissionDate?: Date;
  dischargeDate?: Date;
}

// ── Care Plan ──
export type PlanType =
  | 'SUPPORT_AT_HOME'
  | 'HCP'
  | 'CHSP'
  | 'NATSIFACP'
  | 'NDIS'
  | 'AN_ACC'
  | 'DVA'
  | 'TAC'
  | 'PRIVATE';
export type PlanStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'ACTIVE'
  | 'IN_REVIEW'
  | 'COMPLETED'
  | 'CANCELLED';

export interface CarePlan {
  id: string;
  tenantId: string;
  clientId: string;
  planType: PlanType;
  classificationLevel?: number;
  startDate: Date;
  endDate?: Date;
  reviewDate: Date;
  budgetTotal: number;
  budgetSpent: number;
  budgetRemaining: number;
  careManagementBudget?: number;
  status: PlanStatus;
  version: number;
}

// ── Goal ──
export type GoalStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'ACHIEVED'
  | 'PARTIALLY_ACHIEVED'
  | 'NOT_ACHIEVED'
  | 'DISCONTINUED';
export type GoalCategory =
  | 'health'
  | 'mobility'
  | 'social'
  | 'nutrition'
  | 'cognitive'
  | 'safety'
  | 'independence';

export interface Goal {
  id: string;
  tenantId: string;
  carePlanId: string;
  clientId: string;
  title: string;
  description: string;
  category: GoalCategory;
  targetDate: Date;
  measureCriteria: string;
  progressPercent: number;
  status: GoalStatus;
}

// ── Service Delivery ──
export type ServiceStatus =
  | 'SCHEDULED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'NO_SHOW'
  | 'INCOMPLETE';
export type ClaimStatus =
  | 'UNBILLED'
  | 'DRAFT'
  | 'SUBMITTED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'PAID'
  | 'RECONCILED';
export type ServiceCategory = 'core' | 'clinical' | 'independence' | 'capacity_building';

// ── SAH Service Tiers (Aged Care Rules 2025, Chapter 4) ──
export type SahServiceTier = 'CLINICAL' | 'INDEPENDENCE' | 'EVERYDAY_LIVING';
export type PensionStatus = 'FULL_PENSIONER' | 'PART_PENSIONER' | 'SELF_FUNDED' | 'NOT_ASSESSED';

// ── Industrial Instrument Model (added 2026-04-13) ──
/**
 * Canonical award codes for the aged care workforce.
 * Matches the staff_award_code_check constraint in the database.
 */
export type AwardCode =
  | 'MA000100' // SCHADS Award 2010
  | 'MA000018' // Aged Care Award 2010
  | 'MA000034' // Nurses Award 2020
  | 'MA000027' // Health Professionals and Support Services Award 2020
  | 'ENTERPRISE'; // Enterprise agreement

/**
 * Classification stream within an award instrument.
 */
export type ClassificationStream =
  // SCHADS (MA000100)
  | 'home_care_aged' // Schedule F (HCA1-HCA6)
  | 'home_care_disability' // Schedule E (HCD1-HCD5)
  | 'sacs' // Schedule B (L1-L8)
  // Nurses Award (MA000034)
  | 'nurse'
  | 'aged_care_nurse'
  // Legacy Nurses stream names kept for historical rows / EBA scopes.
  | 'registered_nurse'
  | 'enrolled_nurse'
  | 'nurse_practitioner'
  // HPSS Award (MA000027)
  | 'health_professional'
  | 'support_services'
  | 'apprentice_cooking'
  // Aged Care Award (MA000018)
  | 'direct_care'
  | 'aged_care_employee'
  | 'apprentice_gardening_no_yr12'
  | 'apprentice_gardening_yr12_pre2015'
  | 'apprentice_gardening_yr12_post2015'
  // Enterprise
  | 'enterprise_custom';

// ── SIRS Priority Input (Aged Care Act 2024, s 16 + Aged Care Rules 2025 Ch 5 Pt 3) ──
export interface SirsPriorityInput {
  type: string;
  severity: string;
  injurySustained?: boolean;
  medicalAttentionRequired?: boolean;
  policeNotified?: boolean;
}

export interface GpsLocation {
  lat: number;
  lng: number;
  accuracy: number;
  timestamp: Date;
}

export interface ServiceDelivered {
  id: string;
  tenantId: string;
  clientId: string;
  carePlanId?: string;
  careWorkerId: string;
  serviceType: string;
  serviceCategory: ServiceCategory;
  scheduledStart: Date;
  scheduledEnd: Date;
  actualStart?: Date;
  actualEnd?: Date;
  status: ServiceStatus;
  billableAmount: number;
  claimStatus: ClaimStatus;
  gpsClockIn?: GpsLocation;
  gpsClockOut?: GpsLocation;
}

// ── Staff ──
export type StaffRole =
  | 'REGISTERED_NURSE'
  | 'ENROLLED_NURSE'
  | 'ASSISTANT_IN_NURSING'
  | 'PERSONAL_CARE_WORKER'
  | 'DOMESTIC_WORKER'
  | 'SOCIAL_SUPPORT_WORKER'
  | 'ALLIED_HEALTH_PROFESSIONAL'
  | 'COORDINATOR'
  | 'TEAM_LEADER'
  | 'TRANSPORT_DRIVER'
  | 'CEO'
  | 'OWNER'
  | 'OPERATIONS_DIRECTOR'
  | 'CLINICAL_DIRECTOR'
  | 'FINANCE_DIRECTOR'
  | 'QUALITY_DIRECTOR'
  | 'HR_DIRECTOR'
  | 'REGION_MANAGER'
  | 'REGION_COMPLIANCE_OFFICER'
  | 'REGION_FINANCE_OFFICER'
  | 'FACILITY_MANAGER'
  | 'QUALITY_OFFICER'
  | 'CASE_MANAGER'
  | 'LIFESTYLE_OFFICER'
  | 'CLINICAL_LEAD'
  | 'FINANCE_OFFICER'
  | 'PAYROLL_OFFICER'
  | 'AGENCY_CONTRACTOR'
  | 'HR_OFFICER';
export type EmploymentType = 'FULL_TIME' | 'PART_TIME' | 'CASUAL' | 'CONTRACTOR';
export type ScreeningStatus =
  | 'PENDING'
  | 'CLEARED'
  | 'CONDITIONAL'
  | 'EXCLUDED'
  | 'EXPIRED'
  | 'BLOCKED';

export interface Staff {
  id: string;
  tenantId: string;
  userId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: StaffRole;
  employmentType: EmploymentType;
  awardCode?: AwardCode | null;
  classificationStream?: ClassificationStream | null;
  classificationLevel?: string | null;
  payPoint?: string | null;
  enterpriseAgreementName?: string | null;
  needsAwardReview?: boolean;
  schadsLevel?: string | null;
  hourlyRate?: number | null;
  screeningStatus: ScreeningStatus;
  isActive: boolean;
}

// ── Roster ──
export type ShiftStatus =
  | 'DRAFT'
  | 'PUBLISHED'
  | 'ACCEPTED'
  | 'DECLINED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED';

export interface RosterShift {
  id: string;
  tenantId: string;
  staffId: string;
  clientId?: string;
  serviceType?: string;
  startTime: Date;
  endTime: Date;
  status: ShiftStatus;
  schadsLevel?: string;
  basePayRate: number;
  penaltyRate: number;
  isPublicHoliday: boolean;
  isOvertime: boolean;
}

// ── Incident (SIRS) ──
// Current incident types used across the platform.
// The first 8 values are the current SIRS reportable incident categories under
// Aged Care Act 2024 s 16. `OTHER_SERIOUS` is retained only as a deprecated
// historical value that may still exist in stored incident rows created before
// the 2026-04-13 legislative remediation.
export type SirsIncidentType =
  | 'UNREASONABLE_USE_OF_FORCE'
  | 'UNLAWFUL_SEXUAL_CONTACT'
  | 'PSYCHOLOGICAL_EMOTIONAL_ABUSE'
  | 'STEALING_FINANCIAL_COERCION'
  | 'NEGLECT'
  | 'INAPPROPRIATE_RESTRICTIVE_PRACTICES'
  | 'UNEXPLAINED_ABSENCE'
  | 'UNEXPECTED_DEATH'
  // Deprecated historical value only — do not use for new writes.
  | 'OTHER_SERIOUS'
  | 'FALL'
  | 'MEDICATION_ERROR'
  | 'INFECTION'
  | 'PRESSURE_INJURY'
  | 'BEHAVIOURAL'
  // Care incidents R1 S4 (20435000002184): non-SIRS types.
  | 'SKIN_INJURY'
  | 'UNEXPLAINED_INJURY'
  | 'MEDICAL_EPISODE'
  | 'OTHER';
export type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export type InvestigationStatus =
  | 'REPORTED'
  | 'UNDER_INVESTIGATION'
  | 'ACTION_REQUIRED'
  | 'RESOLVED'
  | 'CLOSED'
  // Restrictive-practice SIRS candidate awaiting human reportability triage
  // (created by trg_notify_restrictive_practice; not yet reportable).
  | 'AWAITING_TRIAGE';

export interface Incident {
  id: string;
  tenantId: string;
  clientId?: string;
  reporterId: string;
  incidentType: SirsIncidentType;
  severity: Severity;
  occurredAt: Date;
  discoveredAt: Date;
  location: string;
  description: string;
  investigationStatus: InvestigationStatus;
  isReportable: boolean;
  reportingDeadline?: Date;
}

// ── Observation ──
export type ObservationType =
  | 'BP_SYSTOLIC'
  | 'BP_DIASTOLIC'
  | 'HEART_RATE'
  | 'SPO2'
  | 'BLOOD_GLUCOSE'
  | 'TEMPERATURE'
  | 'WEIGHT'
  | 'HEIGHT'
  | 'BMI'
  | 'PAIN_SCORE'
  | 'RESPIRATORY_RATE'
  | 'MMSE_SCORE'
  | 'RUDAS_SCORE'
  | 'MOCA_SCORE'
  | 'GCS_SCORE'
  | 'BRADEN_SCORE'
  | 'FALLS_RISK_SCORE'
  | 'MOOD'
  | 'SLEEP_HOURS'
  | 'FLUID_INTAKE'
  | 'FLUID_OUTPUT'
  | 'NUTRITION_INTAKE'
  | 'BOWEL_MOVEMENT'
  | 'WOUND_ASSESSMENT'
  | 'SKIN_INTEGRITY'
  | 'MOBILITY_ASSESSMENT'
  | 'NEWS2'
  | 'NEWS2_SCORE'
  | 'EARLY_WARNING_SCORE';

export interface Observation {
  id: string;
  tenantId: string;
  clientId: string;
  recordedById: string;
  observationType: ObservationType;
  valueNumeric?: number;
  valueText?: string;
  unit?: string;
  isAbnormal: boolean;
  alertTriggered: boolean;
  deviceId?: string;
  recordedAt: Date;
}

// ── Medication ──
export type MedicationStatus = 'ACTIVE' | 'ON_HOLD' | 'DISCONTINUED' | 'COMPLETED';
export type MedAdminStatus =
  | 'SCHEDULED'
  | 'ADMINISTERED'
  | 'REFUSED'
  | 'WITHHELD'
  | 'NOT_AVAILABLE'
  | 'MISSED'
  | 'SELF_ADMINISTERED';

export interface Medication {
  id: string;
  tenantId: string;
  clientId: string;
  medicationName: string;
  genericName?: string;
  strength?: string;
  form: string;
  route: string;
  frequency: string;
  dose: string;
  schedule: string;
  isControlled: boolean;
  isPrn: boolean;
  status: MedicationStatus;
}

// ── IoT Device ──
export type DeviceType =
  | 'BLOOD_PRESSURE_MONITOR'
  | 'PULSE_OXIMETER'
  | 'GLUCOMETER'
  | 'WEIGHT_SCALE'
  | 'THERMOMETER'
  | 'HEART_RATE_MONITOR'
  | 'ACTIVITY_TRACKER'
  | 'SLEEP_TRACKER'
  | 'FALL_DETECTOR'
  | 'GPS_TRACKER'
  | 'DOOR_SENSOR'
  | 'MOTION_SENSOR'
  | 'TEMPERATURE_SENSOR'
  | 'HUMIDITY_SENSOR'
  | 'SMOKE_DETECTOR'
  | 'WATER_LEAK_SENSOR'
  | 'SMART_PILL_DISPENSER'
  | 'PENDANT_ALARM'
  | 'WRIST_ALARM'
  | 'BED_SENSOR';
export type DeviceConnectionStatus = 'ONLINE' | 'OFFLINE' | 'ERROR' | 'MAINTENANCE';
export type DeviceStatus = 'ACTIVE' | 'INACTIVE' | 'MAINTENANCE' | 'DECOMMISSIONED' | 'LOST';

export interface IoTDevice {
  id: string;
  tenantId: string;
  clientId: string;
  deviceType: DeviceType;
  manufacturer: string;
  model: string;
  serialNumber: string;
  mqttTopic: string;
  mqttClientId: string;
  connectionStatus: DeviceConnectionStatus;
  batteryLevel?: number;
  status: DeviceStatus;
}

// ── Assessment ──
export type AssessmentType =
  | 'NSAF'
  | 'ACAT'
  | 'MMSE'
  | 'RUDAS'
  | 'MOCA'
  | 'ADL'
  | 'IADL'
  | 'BRADEN'
  | 'FALLS_RISK'
  | 'NUTRITION'
  | 'PAIN'
  | 'DEPRESSION'
  | 'MOBILITY'
  | 'CONTINENCE'
  | 'BEHAVIOUR'
  | 'SKIN_INTEGRITY'
  | 'MEDICATION_REVIEW'
  | 'COMPREHENSIVE_CARE';
export type AssessmentStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'PENDING_SIGN_OFF'
  | 'SIGNED_OFF'
  | 'REJECTED'
  | 'SUPERSEDED';

export interface Assessment {
  id: string;
  tenantId: string;
  clientId: string;
  assessorId: string;
  assessmentType: AssessmentType;
  totalScore?: number;
  maxScore?: number;
  interpretation?: string;
  status: AssessmentStatus;
}

// ── Assessment Scoring ──
export interface AssessmentScoreResult {
  totalScore: number;
  maxScore: number;
  interpretation: string;
  riskLevel: 'low' | 'moderate' | 'high' | 'severe';
  subscales?: Record<string, { score: number; maxScore: number; interpretation: string }>;
  recommendations?: string[];
}

export interface BradenSubscales {
  sensoryPerception: number; // 1-4
  moisture: number; // 1-4
  activity: number; // 1-4
  mobility: number; // 1-4
  nutrition: number; // 1-4
  frictionShear: number; // 1-3
}

export interface ADLResponses {
  bathing: 0 | 1; // 0=dependent, 1=independent
  dressing: 0 | 1;
  toileting: 0 | 1;
  transferring: 0 | 1;
  continence: 0 | 1;
  feeding: 0 | 1;
}

export interface IADLResponses {
  telephone: 0 | 1;
  shopping: 0 | 1;
  foodPreparation: 0 | 1;
  housekeeping: 0 | 1;
  laundry: 0 | 1;
  transportation: 0 | 1;
  medications: 0 | 1;
  finances: 0 | 1;
}

export interface FallsRiskResponses {
  recentFalls: number; // 0=none, 1=one, 2=two+
  medications: number; // 0=none, 1=sedatives, 2=multiple
  psychologicalStatus: number; // 0=normal, 1=mild, 2=appears impaired
  cognitiveStatus: number; // 0=intact, 1=mildly impaired, 2=impaired
  mobilityAids: number; // 0=none, 1=uses aids, 2=requires assistance
}

export interface MNAResponses {
  appetiteDecline: number; // 0-2
  weightLoss: number; // 0-3
  mobility: number; // 0-2
  psychologicalStress: number; // 0-2
  neuropsychological: number; // 0-2
  bmi: number; // 0-3
}

export interface AbbeyPainResponses {
  vocalisation: 0 | 1 | 2 | 3;
  facialExpression: 0 | 1 | 2 | 3;
  bodyLanguage: 0 | 1 | 2 | 3;
  behaviouralChange: 0 | 1 | 2 | 3;
  physiologicalChange: 0 | 1 | 2 | 3;
  physicalChange: 0 | 1 | 2 | 3;
}

// ── Quality Indicators ──
export interface QualityIndicator {
  id: string;
  name: string;
  value: number;
  unit: string;
  period: { start: Date; end: Date };
  benchmark?: number;
  trend: 'improving' | 'stable' | 'declining';
  previousValue?: number;
}

export interface QualityIndicatorReport {
  tenantId: string;
  reportingPeriod: { start: Date; end: Date };
  indicators: QualityIndicator[];
  generatedAt: Date;
}

// ── Pagination ──
export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ── API Response ──
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
  meta?: Record<string, unknown>;
}

// ── Credential Alert ──
export interface CredentialAlert {
  staffId: string;
  staffName: string;
  credentialType: string;
  expiryDate: Date;
  daysRemaining: number;
  alertLevel: 'INFO' | 'WARNING' | 'URGENT' | 'EXPIRED';
  isMandatory: boolean;
}

// ── Facility (Phase 1) ──
export type FacilityType =
  | 'RESIDENTIAL'
  | 'HOME_CARE_OFFICE'
  | 'DAY_CENTRE'
  | 'RESPITE_CENTRE'
  | 'RETIREMENT_VILLAGE'
  | 'HEAD_OFFICE';
export type FacilityStatus = 'ACTIVE' | 'INACTIVE' | 'SETUP' | 'DECOMMISSIONED';

export interface Facility {
  id: string;
  tenantId: string;
  name: string;
  facilityType: FacilityType;
  status: FacilityStatus;
  napRegistrationId?: string;
  addressLine1: string;
  addressLine2?: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  latitude?: number;
  longitude?: number;
  timezone: string;
  phone?: string;
  email?: string;
  managerId?: string;
  totalBeds?: number;
  operatingBeds?: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface StaffFacility {
  id: string;
  staffId: string;
  facilityId: string;
  isPrimary: boolean;
  canFloat: boolean;
  createdAt: Date;
}

// ── Residential (Phase 2) ──
export type RoomType =
  | 'SINGLE'
  | 'SHARED'
  | 'COMPANION'
  | 'RESPITE'
  | 'PALLIATIVE'
  | 'DEMENTIA_SPECIFIC';
export type RoomStatus = 'AVAILABLE' | 'OCCUPIED' | 'MAINTENANCE' | 'RESERVED' | 'DECOMMISSIONED';
export type BedStatus = 'AVAILABLE' | 'OCCUPIED' | 'RESERVED' | 'MAINTENANCE' | 'BLOCKED';
export type ActivityStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export interface Wing {
  id: string;
  facilityId: string;
  name: string;
  floor?: number;
  wingType?: string;
  capacity?: number;
  createdAt: Date;
}

export interface Room {
  id: string;
  facilityId: string;
  wingId?: string;
  roomNumber: string;
  roomType: RoomType;
  floor?: number;
  bedCount: number;
  hasEnsuite: boolean;
  hasBalcony: boolean;
  isAccessible: boolean;
  features?: Record<string, unknown>;
  status: RoomStatus;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Bed {
  id: string;
  roomId: string;
  bedNumber: string;
  clientId?: string;
  status: BedStatus;
  admittedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface CareMinuteTarget {
  id: string;
  tenantId: string;
  facilityId: string;
  quarterStart: Date;
  quarterEnd: Date;
  rnMinutesTarget: number;
  totalMinutesTarget: number;
  rnMinutesActual?: number;
  totalMinutesActual?: number;
  isCompliant?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Activity {
  id: string;
  tenantId: string;
  facilityId: string;
  name: string;
  description?: string;
  activityType: string;
  location?: string;
  isRecurring: boolean;
  recurrenceRule?: string;
  scheduledDate?: Date;
  startTime: Date;
  endTime: Date;
  maxParticipants?: number;
  facilitatedBy?: string;
  status: ActivityStatus;
  attendees?: unknown[];
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface VisitorLog {
  id: string;
  tenantId: string;
  facilityId: string;
  clientId?: string;
  visitorName: string;
  relationship?: string;
  contactNumber?: string;
  signInTime: Date;
  signOutTime?: Date;
  healthScreened: boolean;
  vaccinationVerified: boolean;
  notes?: string;
  createdAt: Date;
}

export interface NightRound {
  id: string;
  tenantId: string;
  facilityId: string;
  staffId: string;
  roundDate: Date;
  startedAt: Date;
  completedAt?: Date;
  checks: unknown[];
  createdAt: Date;
}

// ── Home Care (Phase 3) ──
export type ServiceAreaStatus = 'ACTIVE' | 'FULL' | 'INACTIVE';

export interface ServiceArea {
  id: string;
  tenantId: string;
  facilityId: string;
  name: string;
  postcodes: string[];
  suburbs: string[];
  boundary?: Record<string, unknown>;
  maxClients?: number;
  status: ServiceAreaStatus;
  createdAt: Date;
  updatedAt: Date;
}

export interface StaffServiceArea {
  id: string;
  staffId: string;
  serviceAreaId: string;
  isPrimary: boolean;
  createdAt: Date;
}

export interface ScheduleTemplate {
  id: string;
  tenantId: string;
  clientId: string;
  name: string;
  entries: unknown[];
  isActive: boolean;
  effectiveFrom: Date;
  effectiveTo?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface HomeAssessment {
  id: string;
  tenantId: string;
  clientId: string;
  assessorId: string;
  assessmentDate: Date;
  accessMethod?: string;
  accessCode?: string;
  accessNotes?: string;
  parkingNotes?: string;
  fallsRisks?: Record<string, unknown>;
  trippingHazards?: Record<string, unknown>;
  bathroomSafety?: Record<string, unknown>;
  kitchenSafety?: Record<string, unknown>;
  fireRisks?: Record<string, unknown>;
  hasStairs?: boolean;
  hasRamp?: boolean;
  hasGrabRails?: boolean;
  hasSmokeAlarm?: boolean;
  petDetails?: string;
  equipmentNeeded?: Record<string, unknown>;
  equipmentInstalled?: Record<string, unknown>;
  overallRiskLevel?: string;
  recommendations?: string;
  nextReviewDate?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// ── Pricing (Phase 4) ──
export type RateCardStatus = 'ACTIVE' | 'DRAFT' | 'EXPIRED';
export type AgreementStatus =
  | 'DRAFT'
  | 'PENDING_SIGNATURE'
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'EXPIRED'
  | 'TERMINATED';

export interface ServiceRateCard {
  id: string;
  tenantId: string;
  name: string;
  fundingProgram: string;
  effectiveFrom: Date;
  effectiveTo?: Date;
  rates: unknown[];
  isDefault: boolean;
  status: RateCardStatus;
  createdAt: Date;
  updatedAt: Date;
}

export interface ServiceAgreement {
  id: string;
  tenantId: string;
  clientId: string;
  agreementNumber: string;
  agreementType: string;
  fundingProgram: string;
  rateCardId?: string;
  startDate: Date;
  endDate?: Date;
  reviewDate?: Date;
  annualBudget?: number;
  contributionRate?: number;
  servicesIncluded: unknown[];
  status: AgreementStatus;
  signedDate?: Date;
  signedBy?: string;
  documentId?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ServicePackage {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  fundingProgram: string;
  services: unknown[];
  weeklyPrice?: number;
  annualPrice?: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ── Config (Phase 5) ──
export interface TenantConfig {
  id: string;
  tenantId: string;
  timezone: string;
  dateFormat: string;
  financialYearStart: string;
  defaultShiftDuration: number;
  rosterWeekStart: number;
  screeningRecheckMonths: number;
  incidentEscalationRules?: Record<string, unknown>;
  defaultPaymentTermsDays: number;
  lateFeeEnabled: boolean;
  lateFeePercent?: number;
  incidentNotifyEmails: string[];
  complianceNotifyEmails: string[];
  createdAt: Date;
  updatedAt: Date;
}

export interface FacilityConfig {
  id: string;
  facilityId: string;
  operatingHoursStart: string;
  operatingHoursEnd: string;
  minimumStaffDay?: number;
  minimumStaffNight?: number;
  minimumRnOnDuty?: number;
  vitalSignAlertThresholds?: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

export interface PublicHoliday {
  id: string;
  tenantId: string;
  name: string;
  date: Date;
  state?: string;
  facilityId?: string;
  isRecurring: boolean;
  createdAt: Date;
}

// ── Workflows (Phase 6) ──
export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'TIMED_OUT' | 'ESCALATED';

export interface ApprovalRule {
  id: string;
  tenantId: string;
  resourceType: string;
  name: string;
  conditions: Record<string, unknown>;
  approvalSteps: unknown[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ApprovalRequest {
  id: string;
  tenantId: string;
  approvalRuleId: string;
  resourceType: string;
  resourceId: string;
  requestedBy: string;
  currentStep: number;
  status: ApprovalStatus;
  responses?: unknown[];
  createdAt: Date;
  updatedAt: Date;
}

export interface EscalationRule {
  id: string;
  tenantId: string;
  incidentType?: string;
  priority?: number;
  steps: unknown[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface NotificationTemplate {
  id: string;
  tenantId: string;
  name: string;
  channel: string;
  eventType: string;
  subject?: string;
  body: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ── Reporting (Phase 7) ──
export interface SavedReport {
  id: string;
  tenantId: string;
  createdBy: string;
  name: string;
  description?: string;
  dataSource: string;
  columns: unknown[];
  filters?: Record<string, unknown>;
  groupBy?: string;
  sortBy?: unknown[];
  isScheduled: boolean;
  scheduleFrequency?: string;
  scheduleRecipients: string[];
  lastRunAt?: Date;
  isShared: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface DashboardLayout {
  id: string;
  tenantId: string;
  userId?: string;
  name: string;
  widgets: unknown[];
  isDefault: boolean;
  createdAt: Date;
  updatedAt: Date;
}

// ── Import (Phase 8) ──
export type ImportStatus = 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';

export interface ImportJob {
  id: string;
  tenantId: string;
  importedBy: string;
  importType: string;
  fileName: string;
  totalRows: number;
  successRows: number;
  errorRows: number;
  errors?: unknown[];
  status: ImportStatus;
  startedAt: Date;
  completedAt?: Date;
}

// ── Consolidation (Phase 10) ──
export type ConsentStatus = 'GIVEN' | 'WITHDRAWN' | 'PENDING';

export interface DocumentLink {
  id: string;
  documentId: string;
  resourceType: string;
  resourceId: string;
  createdAt: Date;
}

export interface Consent {
  id: string;
  tenantId: string;
  clientId: string;
  consentType: string;
  status: ConsentStatus;
  givenDate?: Date;
  withdrawnDate?: Date;
  givenBy?: string;
  witnessedBy?: string;
  documentId?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ── Core Models (additional) ──
export interface Claim {
  id: string;
  tenantId: string;
  clientId: string;
  serviceDeliveredId?: string;
  fundingProgram: string;
  claimType: string;
  invoiceNumber?: string;
  amount: number;
  clientContribution: number;
  governmentSubsidy: number;
  submissionDate?: Date;
  submissionReference?: string;
  status: ClaimStatus;
  rejectionReason?: string;
  rejectionCode?: string;
  paymentDate?: Date;
  paymentReference?: string;
  paymentAmount?: number;
  reconciled: boolean;
  reconciledAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface AuditLog {
  id: string;
  tenantId: string;
  userId: string;
  action: string;
  resource: string;
  resourceId?: string;
  changes?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  metadata?: Record<string, unknown>;
  createdAt: Date;
}

export interface Document {
  id: string;
  tenantId: string;
  clientId?: string;
  uploadedBy: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  storagePath: string;
  category: string;
  isDeleted: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Message {
  id: string;
  tenantId: string;
  threadId: string;
  senderId: string;
  recipientId: string;
  content: string;
  isRead: boolean;
  readAt?: Date;
  createdAt: Date;
}

export interface MedicationAdministration {
  id: string;
  tenantId: string;
  clientId: string;
  medicationId: string;
  administeredById: string;
  scheduledTime: Date;
  administeredAt?: Date;
  status: MedAdminStatus;
  dose: string;
  route: string;
  site?: string;
  refusalReason?: string;
  notes?: string;
  witnessId?: string;
  createdAt: Date;
}

export interface NotificationPreference {
  id: string;
  tenantId: string;
  userId: string;
  category: string;
  email: boolean;
  sms: boolean;
  push: boolean;
  inApp: boolean;
}

export interface NotificationDelivery {
  id: string;
  tenantId: string;
  userId: string;
  channel: string;
  title: string;
  body: string;
  status: string;
  sentAt?: Date;
  deliveredAt?: Date;
  readAt?: Date;
  failureReason?: string;
  retryCount: number;
  maxRetries: number;
  createdAt: Date;
}

export interface TelehealthSession {
  id: string;
  tenantId: string;
  clientId: string;
  clinicianId: string;
  scheduledStart: Date;
  scheduledEnd: Date;
  actualStart?: Date;
  actualEnd?: Date;
  status: string;
  roomName: string;
  recordingEnabled: boolean;
  recordingUrl?: string;
  notes?: string;
  consentGiven: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ClientContribution {
  id: string;
  tenantId: string;
  clientId: string;
  date: Date;
  basicDailyFee: number;
  incomeTestedFee: number;
  hardshipReduction: number;
  effectiveFee: number;
  paidAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface HardshipProvision {
  id: string;
  tenantId: string;
  clientId: string;
  reductionPercent: number;
  reason: string;
  approvedBy: string;
  startDate: Date;
  endDate?: Date;
  isActive: boolean;
  createdAt: Date;
}

export interface ControlledDrugRegister {
  id: string;
  tenantId: string;
  medicationId: string;
  clientId: string;
  action: string;
  quantity: number;
  balanceBefore: number;
  balanceAfter: number;
  witnessedBy: string;
  administeredBy: string;
  batchNumber?: string;
  expiryDate?: Date;
  discrepancyNoted: boolean;
  discrepancyDetail?: string;
  notes?: string;
  recordedAt: Date;
}

export interface AdverseDrugReaction {
  id: string;
  tenantId: string;
  clientId: string;
  medicationId: string;
  reactionType: string;
  severity: string;
  description: string;
  onsetDate: Date;
  resolvedDate?: Date;
  actionTaken: string;
  reportedBy: string;
  reportedToGp: boolean;
  reportedToTga: boolean;
  createdAt: Date;
}

export interface MedicationReconciliation {
  id: string;
  tenantId: string;
  clientId: string;
  event: string;
  preMedications: unknown[];
  postMedications: unknown[];
  discrepancies: unknown[];
  performedBy: string;
  performedAt: Date;
  notes?: string;
  createdAt: Date;
}

export interface IncidentEscalation {
  id: string;
  tenantId: string;
  incidentId: string;
  level: number;
  reason: string;
  notifiedTo: string[];
  sentAt: Date;
}

export interface LeaveBalance {
  id: string;
  tenantId: string;
  staffId: string;
  leaveType: string;
  accrued: number;
  taken: number;
  balance: number;
  asAtDate: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface LeaveRequest {
  id: string;
  tenantId: string;
  staffId: string;
  leaveType: string;
  startDate: Date;
  endDate: Date;
  totalHours: number;
  reason?: string;
  status: string;
  approvedBy?: string;
  approvedAt?: Date;
  declineReason?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface Immunisation {
  id: string;
  tenantId: string;
  clientId: string;
  vaccineName: string;
  vaccineCode?: string;
  doseNumber: number;
  administeredDate: Date;
  administeredBy?: string;
  lotNumber?: string;
  expiryDate?: Date;
  siteOfAdmin?: string;
  status: string;
  nextDueDate?: Date;
  createdAt: Date;
}

// SAH_ANNUAL_BUDGETS has been consolidated to @statura/utils/regulatory-rates.ts
// Import from '@statura/utils' instead of '@statura/types'

// ── Regulated enum registry (vocabulary-mismatch structural gate) ──
export * from './regulated-enums';

// ── Communication vocabulary shared by the mobile and web writers ──
export * from './communication-vocabulary';

// ── Clinical note review outcomes (lockstep with the DB CHECK) ──
export * from './clinical-note-review';
